import { LayoutType } from "app/layout/store";
import { AccountType } from "shared/constants/enums";
import lazyWithRetry from "shared/helpers/lazyWithRetry";
import routeNames from "shared/routes/constants/routeNames";
import RouteValue from "shared/routes/models/routesValue";
import {
    CompanyUserPermissions,
    InternalUserPermissions,
    viewCustomerUserInternalPermissions,
} from "shared/constants/permissions";

const Login = lazyWithRetry(() => import("app/spaceStone/signIn/Login"));
const SignUp = lazyWithRetry(() => import("app/spaceStone/signUp"));
const SignUpContinue = lazyWithRetry(() => import("app/spaceStone/signUp/steps/continueSignUp"));
const SignUpContinueLink = lazyWithRetry(() => import("app/spaceStone/signUp/steps/continueSignUp/ContinueSignUpLink"));
const MyProfile = lazyWithRetry(() => import("app/spaceStone/myProfile"));
const Home = lazyWithRetry(() => import("app/dashboard"));
const ChangeEmailConfirmation = lazyWithRetry(() => import("app/spaceStone/myProfile/changeEmailConfirmation"));
const ExpiredLoginLink = lazyWithRetry(() => import("app/errors/ExpiredLinkErrorPage/expiredLoginLink"));
const ExpiredSignUpLink = lazyWithRetry(() => import("app/errors/ExpiredLinkErrorPage/expiredSignUpLink"));
const ExpiredChangeEmailLink = lazyWithRetry(() => import("app/errors/ExpiredLinkErrorPage/expiredChangeEmailLink"));
const PageNotFoundErrorLink = lazyWithRetry(() => import("app/errors/NotFoundErrorPage"));
const AccessErrorPage = lazyWithRetry(() => import("app/errors/AccessErrorPage"));
const InternalErrorPage = lazyWithRetry(() => import("app/errors/InternalErrorPage"));
const StartReleaseRequest = lazyWithRetry(() => import("app/timeStone/properties/releaseRequests/start"));
const ReleaseRequestDetails = lazyWithRetry(() => import("app/timeStone/properties/releaseRequests/details"));
const ResolutionPanelWithReleaseRequests = lazyWithRetry(
    () => import("app/timeStone/properties/releaseRequests/resolutionPanel")
);
const ReleaseRequestAllEvidence = lazyWithRetry(
    () => import("app/timeStone/properties/releaseRequests/details/allEvidence")
);
const Chat = lazyWithRetry(() => import("app/timeStone/chat"));
const ProposeNewSettlements = lazyWithRetry(() => import("app/timeStone/properties/releaseRequests/newSettlements"));
const StartClaim = lazyWithRetry(() => import("app/timeStone/properties/claims/start"));
const CompanyDetails = lazyWithRetry(() => import("app/spaceStone/companyDetails"));
const AdminUsersList = lazyWithRetry(() => import("app/administration/users/list"));
const AdminUsersProfile = lazyWithRetry(() => import("app/administration/users/profile"));
const AdminUserCreate = lazyWithRetry(() => import("app/administration/users/create"));
const AdminStaffProfile = lazyWithRetry(() => import("app/administration/business/profile/tabs/staffProfile"));
const AdminFinance = lazyWithRetry(() => import("app/administration/finance"));
const AdminSettings = lazyWithRetry(() => import("app/administration/settings"));
const AdminBusinesses = lazyWithRetry(() => import("app/administration/business"));
const AdminBusinessesProfile = lazyWithRetry(() => import("app/administration/business/profile"));
const AdminProperties = lazyWithRetry(() => import("app/administration/properties"));
const AdminUserProfile = lazyWithRetry(() => import("app/administration/settings/internalUserProfile"));
const ResolutionPanelWithTwoTabs = lazyWithRetry(
    () => import("app/timeStone/properties/releaseRequests/resolutionPanelWithTabs")
);
const Staff = lazyWithRetry(() => import("app/spaceStone/staff"));
const StaffUser = lazyWithRetry(() => import("app/spaceStone/staff/user"));
const Properties = lazyWithRetry(() => import("app/spaceStone/properties"));
const AddProperty = lazyWithRetry(() => import("app/spaceStone/properties/addProperty"));
const RevokedInviteLink = lazyWithRetry(() => import("app/errors/ExpiredLinkErrorPage/revokedInviteLink"));
const ExpiredInviteLink = lazyWithRetry(() => import("app/errors/ExpiredLinkErrorPage/expiredInviteLink"));
const TenantMyHome = lazyWithRetry(() => import("app/spaceStone/tenant/myHome"));
const TenantBilling = lazyWithRetry(() => import("app/spaceStone/tenant/billing"));
const Property = lazyWithRetry(() => import("app/spaceStone/properties/property"));
const PdfLoader = lazyWithRetry(() => import("components/pdfLoader"));
const StartEndOfTenancy = lazyWithRetry(() => import("app/spaceStone/properties/endOfTenancy/start"));
const AdminActivity = lazyWithRetry(() => import("app/administration/activity"));
const UserActivity = lazyWithRetry(() => import("app/spaceStone/activity"));
const MissingDetails = lazyWithRetry(() => import("app/spaceStone/missingDetails"));

const routes: RouteValue[] = [
    {
        name: routeNames.DASHBOARD,
        path: routeNames.DASHBOARD,
        isPrivate: true,
        component: Home,
        onlyForAccountTypes: [
            AccountType.Admin,
            AccountType.Agent,
            AccountType.CorporateLandlord,
            AccountType.PrivateLandlord,
        ],
    },
    {
        name: routeNames.MISSING_DETAILS,
        path: routeNames.MISSING_DETAILS,
        isPrivate: true,
        component: MissingDetails,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.LOGIN,
        path: routeNames.LOGIN,
        isPrivate: false,
        component: Login,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.SIGN_UP.SIGN_UP,
        path: routeNames.SIGN_UP.SIGN_UP,
        isPrivate: false,
        component: SignUp,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.SIGN_UP.SIGN_UP_LINK,
        path: routeNames.SIGN_UP.SIGN_UP_LINK,
        isPrivate: false,
        component: SignUpContinueLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.SIGN_UP.SIGN_UP_CONTINUE,
        path: routeNames.SIGN_UP.SIGN_UP_CONTINUE,
        isPrivate: false,
        component: SignUpContinue,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ERRORS.EXPIRED_CONF_LINK,
        path: routeNames.ERRORS.EXPIRED_CONF_LINK,
        isPrivate: false,
        component: ExpiredLoginLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ERRORS.EXPIRED_REG_LINK,
        path: routeNames.ERRORS.EXPIRED_REG_LINK,
        isPrivate: false,
        component: ExpiredSignUpLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ERRORS.NOT_FOUND,
        path: routeNames.ERRORS.NOT_FOUND,
        isPrivate: false,
        component: PageNotFoundErrorLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ERRORS.FORBIDDEN,
        path: routeNames.ERRORS.FORBIDDEN,
        isPrivate: false,
        component: AccessErrorPage,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ERRORS.INTERNAL_ERROR,
        path: routeNames.ERRORS.INTERNAL_ERROR,
        isPrivate: false,
        component: InternalErrorPage,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.RESOLUTION_PANEL.START_RELEASE_REQUEST.ROOT_OR_CONTINUE,
        path: routeNames.RESOLUTION_PANEL.START_RELEASE_REQUEST.ROOT_OR_CONTINUE,
        isPrivate: true,
        component: StartReleaseRequest,
        onlyForAccountTypes: [AccountType.Agent, AccountType.PrivateLandlord, AccountType.CorporateLandlord],
        exact: true,
    },
    {
        name: routeNames.RESOLUTION_PANEL.RELEASE_REQUEST.PROPOSE_NEW_SETTLEMENTS,
        path: routeNames.RESOLUTION_PANEL.RELEASE_REQUEST.PROPOSE_NEW_SETTLEMENTS,
        isPrivate: true,
        component: ProposeNewSettlements,
        exact: true,
    },
    {
        name: routeNames.RESOLUTION_PANEL.RELEASE_REQUEST.ALL_EVIDENCE,
        path: routeNames.RESOLUTION_PANEL.RELEASE_REQUEST.ALL_EVIDENCE,
        isPrivate: true,
        layout: LayoutType.FullWidthLayout,
        component: ReleaseRequestAllEvidence,
    },
    {
        name: routeNames.RESOLUTION_PANEL.RELEASE_REQUEST.HOME,
        path: routeNames.RESOLUTION_PANEL.RELEASE_REQUEST.HOME,
        isPrivate: true,
        component: ReleaseRequestDetails,
    },
    {
        name: routeNames.RESOLUTION_PANEL.ROOT,
        path: routeNames.RESOLUTION_PANEL.ROOT,
        isPrivate: true,
        // Shared component should be without tabs, so use tabs only inside routeByAccountType
        component: ResolutionPanelWithTwoTabs,
        exact: false,
        routeByAccountType: {
            [AccountType.Agent]: {
                component: ResolutionPanelWithReleaseRequests,
            },
            [AccountType.CorporateLandlord]: {
                component: ResolutionPanelWithReleaseRequests,
            },
            [AccountType.PrivateLandlord]: {
                component: ResolutionPanelWithReleaseRequests,
            },
        },
    },
    {
        name: routeNames.MY_PROFILE,
        path: routeNames.MY_PROFILE,
        isPrivate: true,
        component: MyProfile,
    },
    {
        name: routeNames.CHANGE_EMAIL,
        path: routeNames.CHANGE_EMAIL,
        isPrivate: false,
        component: ChangeEmailConfirmation,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ERRORS.EXPIRED_CHANGE_EMAIL_LINK,
        path: routeNames.ERRORS.EXPIRED_CHANGE_EMAIL_LINK,
        isPrivate: false,
        component: ExpiredChangeEmailLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.COMPANY.DEFAULT,
        path: routeNames.COMPANY.DEFAULT,
        isPrivate: true,
        component: CompanyDetails,
        onlyForAccountTypes: [AccountType.Agent, AccountType.CorporateLandlord],
    },
    {
        name: routeNames.ADMIN.USERS.ROOT,
        path: routeNames.ADMIN.USERS.ROOT,
        exact: true,
        isPrivate: true,
        component: AdminUsersList,
        isTolerantPermissions: viewCustomerUserInternalPermissions.isTolerant,
        permission: viewCustomerUserInternalPermissions.permissions,
    },
    {
        name: routeNames.ADMIN.USERS.CREATE_USER,
        path: routeNames.ADMIN.USERS.CREATE_USER,
        exact: true,
        isPrivate: true,
        component: AdminUserCreate,
        permission: [InternalUserPermissions.CreateNewCustomerUserUsers],
    },
    {
        name: routeNames.ADMIN.USERS.USER_PROFILE,
        path: routeNames.ADMIN.USERS.USER_PROFILE,
        isPrivate: true,
        component: AdminUsersProfile,
        isTolerantPermissions: viewCustomerUserInternalPermissions.isTolerant,
        permission: viewCustomerUserInternalPermissions.permissions,
    },
    {
        name: routeNames.ADMIN.BUSINESSES.STAFF_PROFILE.ROOT,
        path: routeNames.ADMIN.BUSINESSES.STAFF_PROFILE.ROOT,
        isPrivate: true,
        component: AdminStaffProfile,
    },
    {
        name: routeNames.ADMIN.FINANCE.ROOT,
        path: routeNames.ADMIN.FINANCE.ROOT,
        isPrivate: true,
        component: AdminFinance,
    },
    {
        name: routeNames.ADMIN.VOUCHER_INVOICE,
        path: routeNames.ADMIN.VOUCHER_INVOICE,
        isPrivate: true,
        component: PdfLoader,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.ADMIN.SETTINGS.PROFILE.ROOT,
        path: routeNames.ADMIN.SETTINGS.PROFILE.ROOT,
        isPrivate: true,
        component: AdminUserProfile,
        isTolerantPermissions: true,
        permission: [
            InternalUserPermissions.EditInternalUserAdminUsers,
            InternalUserPermissions.ViewSelectedInternalUserAdminUsers,
            InternalUserPermissions.DeactivateActivateInternalUserUsers,
        ],
    },
    {
        name: routeNames.ADMIN.SETTINGS.ROOT,
        path: routeNames.ADMIN.SETTINGS.ROOT,
        isPrivate: true,
        component: AdminSettings,
    },
    {
        name: routeNames.ADMIN.BUSINESSES.PROFILE.HOME,
        path: routeNames.ADMIN.BUSINESSES.PROFILE.HOME,
        isPrivate: true,
        component: AdminBusinessesProfile,
        isTolerantPermissions: true,
        permission: [
            InternalUserPermissions.ViewCompaniesBusinesses,
            InternalUserPermissions.EditCompanyDetailsBusinesses,
        ],
    },
    {
        name: routeNames.ADMIN.BUSINESSES.ROOT,
        path: routeNames.ADMIN.BUSINESSES.ROOT,
        isPrivate: true,
        component: AdminBusinesses,
        isTolerantPermissions: true,
        permission: [
            InternalUserPermissions.ViewCompaniesBusinesses,
            InternalUserPermissions.EditCompanyDetailsBusinesses,
        ],
    },
    {
        name: routeNames.STAFF.ROOT,
        path: routeNames.STAFF.ROOT,
        isPrivate: true,
        component: Staff,
        onlyForAccountTypes: [AccountType.Agent, AccountType.CorporateLandlord],
        permission: [CompanyUserPermissions.ViewUsersStaff, CompanyUserPermissions.EditUsersStaff],
        isTolerantPermissions: true,
    },
    {
        name: routeNames.STAFF.USER,
        path: routeNames.STAFF.USER,
        isPrivate: true,
        component: StaffUser,
    },
    {
        name: routeNames.PROPERTIES.CREATE_PROPERTY_ROOT,
        path: routeNames.PROPERTIES.CREATE_PROPERTY_ROOT,
        isPrivate: true,
        component: AddProperty,
    },
    {
        name: routeNames.PROPERTIES.CLAIMS.START,
        path: routeNames.PROPERTIES.CLAIMS.START,
        isPrivate: true,
        component: StartClaim,
    },
    {
        name: routeNames.PROPERTIES.END_OF_TENANCY.CREATE,
        path: routeNames.PROPERTIES.END_OF_TENANCY.CREATE,
        isPrivate: true,
        exact: true,
        onlyForAccountTypes: [
            AccountType.Agent,
            AccountType.PrivateLandlord,
            AccountType.CorporateLandlord,
            AccountType.Admin,
        ],
        component: StartEndOfTenancy,
    },
    {
        name: routeNames.TENANT_END_OF_TENANCY,
        path: routeNames.TENANT_END_OF_TENANCY,
        isPrivate: true,
        exact: true,
        component: StartEndOfTenancy,
        onlyForAccountTypes: [AccountType.Tenant],
    },
    {
        name: routeNames.PROPERTY.ROOT,
        path: routeNames.PROPERTY.ROOT,
        isPrivate: true,
        component: Property,
    },
    {
        name: routeNames.PROPERTIES.ROOT,
        path: routeNames.PROPERTIES.ROOT,
        isPrivate: true,
        component: Properties,
        routeByAccountType: {
            [AccountType.Admin]: {
                component: AdminProperties,
            },
        },
    },
    {
        name: routeNames.MEMBERSHIP_CERTIFICATE,
        path: routeNames.MEMBERSHIP_CERTIFICATE,
        isPrivate: true,
        component: PdfLoader,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.CHAT,
        path: routeNames.CHAT,
        isPrivate: false,
        component: Chat,
    },
    {
        name: routeNames.INVITATIONS.REVOKE_INVITE,
        path: routeNames.INVITATIONS.REVOKE_INVITE,
        isPrivate: false,
        component: RevokedInviteLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.INVITATIONS.EXPIRED_INVITE,
        path: routeNames.INVITATIONS.EXPIRED_INVITE,
        isPrivate: false,
        component: ExpiredInviteLink,
        layout: LayoutType.FullWidthLayout,
    },
    {
        name: routeNames.TENANT_HOME.HOME,
        path: routeNames.TENANT_HOME.HOME,
        isPrivate: true,
        component: TenantMyHome,
    },
    {
        name: routeNames.TENANT_BILLING.HOME,
        path: routeNames.TENANT_BILLING.HOME,
        isPrivate: true,
        component: TenantBilling,
    },
    {
        name: routeNames.ACTIVITY,
        path: routeNames.ACTIVITY,
        isPrivate: true,
        exact: true,
        component: UserActivity,
        routeByAccountType: {
            [AccountType.Admin]: {
                component: AdminActivity,
                permission: InternalUserPermissions.ViewActivity,
            },
            [AccountType.CorporateLandlord]: {
                component: UserActivity,
                permission: CompanyUserPermissions.ViewActivity,
            },
            [AccountType.Agent]: {
                component: UserActivity,
                permission: CompanyUserPermissions.ViewActivity,
            },
        },
    },
];

export default routes;
